import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Select,
  MenuItem,
  IconButton,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  CircularProgress,
  TextField,
  Tooltip,
  Chip,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import { toast } from 'react-toastify';
import useAPICall from '../../hooks/useAPICall';
import { API_CONFIG, buildEndpoint } from '../../config/api.config';
import GlobalMaterialTable from '../../components/common/GlobalMaterialTable';
import ConfirmDeleteDialog from '../../components/common/ConfirmDeleteDialog';
import PageBreadcrumbs from '../../components/common/PageBreadcrumbs';
import moment from 'moment';
import apiClient from '../../services/apiClient';

const FeedbackInbox = () => {
  const navigate = useNavigate();
  const { loading, data, execute } = useAPICall();
  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });

  const [editingRow, setEditingRow] = useState(null);
  const [editState, setEditState] = useState({ feedback_type: '', status: '', feedback: '' });
  const [isSaving, setIsSaving] = useState(false);
  const [deletingRow, setDeletingRow] = useState(null);
  const [formErrors, setFormErrors] = useState({});

  const softTextFieldSx = {
    '& .MuiOutlinedInput-root': {
      backgroundColor: '#fff',
      borderRadius: '4px',
      height: '38px',
      transition: 'all 0.15s ease',
      '& fieldset': {
        borderColor: '#e2e8f0',
        borderWidth: '1px',
      },
      '&:hover fieldset': {
        borderColor: '#cbd5e1',
      },
      '&.Mui-focused fieldset': {
        borderColor: '#2196f3',
        borderWidth: '1px',
      },
      '&.Mui-focused': {
        backgroundColor: '#fff',
        boxShadow: '0 2px 6px rgba(33, 150, 243, 0.06)',
      },
    },
    '& .MuiOutlinedInput-input': {
      padding: '6px 10px',
      color: '#0f172a',
      height: '38px',
      boxSizing: 'border-box',
    },
    '& .MuiSelect-select': {
      padding: '0 10px !important',
      display: 'flex',
      alignItems: 'center',
      height: '38px',
      color: '#0f172a',
      boxSizing: 'border-box',
    },
  };

  const softMultilineFieldSx = {
    ...softTextFieldSx,
    '& .MuiOutlinedInput-root': {
      ...softTextFieldSx['& .MuiOutlinedInput-root'],
      height: 'auto',
      minHeight: '38px',
    },
    '& .MuiOutlinedInput-input': {
      ...softTextFieldSx['& .MuiOutlinedInput-input'],
      height: 'auto',
      minHeight: '38px',
    },
  };

  const loadFeedback = async () => {
    try {
      await execute(API_CONFIG.dcr.getLlmFeedback, null, {
        errorMessage: 'Unable to load feedback.',
        retries: 1,
      });
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    loadFeedback();
  }, [execute]);

  useEffect(() => {
    if (data) {
      if (data.llmFeedback) {
        setRows(data.llmFeedback);
      } else if (Array.isArray(data)) {
        setRows(data);
      } else if (data.data && Array.isArray(data.data)) {
        setRows(data.data);
      } else if (data.data?.llmFeedback) {
        setRows(data.data.llmFeedback);
      }
    }
  }, [data]);

  const handleEditClick = (row) => {
    setEditingRow(row);
    setEditState({
      feedback_type: row.feedback_type || '',
      status: row.status || 'open',
      feedback: row.feedback || '',
    });
    setFormErrors({});
  };

  const handleSaveEdit = async () => {
    const errors = {};
    if (!editState.feedback_type) errors.feedback_type = 'Category is required';
    if (!editState.status) errors.status = 'Status is required';

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    setFormErrors({});

    setIsSaving(true);
    try {
      const payload = { ...editingRow, ...editState };
      const endpoint = buildEndpoint(API_CONFIG.dcr.updateLlmFeedback.endpoint, {
        feedbackId: editingRow.feedback_id,
      });
      await apiClient.put(endpoint, payload);
      toast.success('Feedback updated successfully');
      setEditingRow(null);
      await loadFeedback();
    } catch (error) {
      toast.error('Failed to update feedback');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deletingRow) return;
    try {
      const endpoint = buildEndpoint(API_CONFIG.dcr.deleteLlmFeedback.endpoint, {
        feedbackId: deletingRow.feedback_id,
      });
      await apiClient.delete(endpoint);
      toast.success('Feedback deleted successfully');
      setDeletingRow(null);
      await loadFeedback();
    } catch (error) {
      toast.error('Failed to delete feedback');
    }
  };

  const formatTimestamp = (value) => (value ? moment(value).format('YYYY-MM-DD HH:mm:ss') : '-');

  const columns = useMemo(
    () => [
      {
        header: 'Request ID',
        id: 'request_id',
        accessorFn: (row) => row.request_id,
        size: 100,
        Cell: ({ cell }) => (
          <Typography
            variant="body2"
            sx={{ color: '#1d8ef0', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
          >
            {cell.getValue()}
          </Typography>
        ),
      },
      {
        header: 'Request Type',
        id: 'workflow_name',
        accessorFn: (row) => row.workflow_name || 'address-change',
        size: 140,
      },
      {
        header: 'User',
        id: 'username',
        accessorFn: (row) => row.username || 'System User',
        size: 120,
      },
      {
        header: 'Reaction / target',
        id: 'reaction_target',
        size: 180,
        Cell: ({ row }) => {
          const reaction = row.original.reaction || 'Needs work';
          const target = row.original.slot_key || row.original.target || 'Recommendation';
          const isNeedsWork =
            reaction.toLowerCase() === 'needs_work' || reaction.toLowerCase() === 'needs work';
          return (
            <Stack spacing={0}>
              <Typography
                variant="body2"
                sx={{ color: isNeedsWork ? '#ef4444' : '#10b981', textTransform: 'capitalize' }}
              >
                {reaction.replace('_', ' ')}
              </Typography>
              <Typography variant="caption" sx={{ color: '#94a3b8', textTransform: 'capitalize' }}>
                {target.split('.').pop().replace('_', ' ')}
              </Typography>
            </Stack>
          );
        },
      },
      {
        header: 'Feedback',
        id: 'feedback',
        accessorFn: (row) => row.feedback || row.comment || '',
        size: 250,
        Cell: ({ cell }) => (
          <Typography
            variant="body2"
            sx={{
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              maxWidth: 230,
            }}
          >
            {cell.getValue()}
          </Typography>
        ),
      },
      {
        header: 'Category',
        id: 'category',
        size: 160,
        Cell: ({ row }) => {
          const cat = row.original.category || row.original.feedback_type;
          if (!cat) {
            return (
              <Typography variant="body2" sx={{ color: '#94a3b8' }}>
                Not yet classified
              </Typography>
            );
          }
          if (
            cat.toLowerCase() === 'agent_prompting' ||
            cat === 'Agent Prompting' ||
            cat === 'agent prompting'
          ) {
            return (
              <Stack spacing={0.5} alignItems="flex-start">
                <Chip
                  label="Agent prompting"
                  size="small"
                  sx={{
                    bgcolor: alpha('#1d8ef0', 0.1),
                    color: '#1d8ef0',
                    borderRadius: '16px',
                    fontWeight: 500,
                    height: 24,
                    border: 'none',
                  }}
                />
                {row.original.resolved_rule_id && (
                  <Typography
                    variant="caption"
                    sx={{
                      color: '#1d8ef0',
                      cursor: 'pointer',
                      '&:hover': { textDecoration: 'underline' },
                    }}
                  >
                    Rule #{row.original.resolved_rule_id}
                  </Typography>
                )}
              </Stack>
            );
          }
          return (
            <Chip
              label={cat}
              size="small"
              sx={{ bgcolor: '#f1f5f9', color: '#64748b', height: 24, border: 'none' }}
            />
          );
        },
      },
      {
        header: 'Status',
        id: 'status',
        accessorFn: (row) => row.status || 'open',
        size: 120,
        Cell: ({ cell, row }) => {
          const val = cell.getValue();
          let displayVal = val;
          let bgcolor = '#f1f5f9';
          let color = '#475569';
          if (val === 'closed') {
            displayVal = 'Closed';
            bgcolor = '#ECFDF5';
            color = '#16A34A';
          } else if (val === 'open') {
            displayVal = 'Open';
            bgcolor = '#f1f5f9';
            color = '#64748b';
          } else if (val === 'in_progress') {
            displayVal = 'In progress';
            bgcolor = '#FFF7ED';
            color = '#EA580C';
          }
          return (
            <Box sx={{ width: '100%' }}>
              <Stack spacing={0.5} alignItems="flex-start">
                <Chip
                  label={displayVal}
                  size="small"
                  sx={{
                    bgcolor,
                    color,
                    fontWeight: 500,
                    height: 24,
                    borderRadius: '16px',
                    textTransform: 'capitalize',
                  }}
                />

                {/* {(val === "closed") && (
                <Box sx={{ width: "fit-content",}}>
                  <Typography
                    variant="caption"
                    sx={{
                      color: "#94a3b8",
                      fontSize: 11,
                      ml:1
                    }}
                  >
                    by {row.original.resolved_by || "System"}
                  </Typography>
                </Box>
              )} */}
              </Stack>
            </Box>
          );
        },
      },
      {
        header: 'Created',
        id: 'created_at',
        size: 140,
        Cell: ({ row }) => {
          const date = formatTimestamp(row.original.created_at);
          return (
            <Stack spacing={0}>
              <Typography variant="body2">{date}</Typography>
            </Stack>
          );
        },
      },
      {
        header: 'Actions',
        id: 'actions',
        size: 100,
        Cell: ({ row }) => (
          <Stack direction="row" spacing={1} justifyContent={'center'}>
            <IconButton
              size="small"
              color="error"
              onClick={(e) => {
                e.stopPropagation();
                setDeletingRow(row.original);
              }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Stack>
        ),
      },
    ],
    []
  );

  const hasChanges =
    editingRow &&
    (editState.feedback_type !== (editingRow.feedback_type || '') ||
      editState.status !== (editingRow.status || 'open'));

  return (
    <Box sx={{ p: 0 }}>
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Paper
            sx={{ boxShadow: '0 1px 3px rgba(0,0,0,0.1)', borderRadius: 2, overflow: 'hidden' }}
          >
            <GlobalMaterialTable
              isLoading={loading}
              columns={columns}
              data={rows}
              emptyMessage="No Feedback found"
              enablePagination
              enableBottomToolbar
              manualPagination={false}
              page={pagination.pageIndex + 1}
              pageSize={pagination.pageSize}
              onPageChange={(p) => setPagination((prev) => ({ ...prev, pageIndex: p - 1 }))}
              onPageSizeChange={(ps) => setPagination((prev) => ({ ...prev, pageSize: ps }))}
              tableTitle="Feedback Inbox"
              tableCount={rows.length}
              muiTableBodyRowProps={({ row }) => ({
                onClick: () =>
                  navigate(
                    `/feedback/${
                      row.original.feedback_id || row.original.id || row.original.request_id
                    }`
                  ),
                sx: { cursor: 'pointer' },
              })}
              muiTablePaperProps={{
                elevation: 0,
                sx: {
                  border: 'none !important',
                  boxShadow: 'none !important',
                },
              }}
            />
          </Paper>
        </Box>
      </Box>

      <Dialog
        open={!!editingRow}
        onClose={() => {
          if (!isSaving) setEditingRow(null);
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle
          sx={{
            fontWeight: 700,
            borderBottom: '1px solid #e2e8f0',
            pb: 2,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          Edit Feedback
          <IconButton
            onClick={() => {
              if (!isSaving) setEditingRow(null);
            }}
            size="small"
            disabled={isSaving}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Stack spacing={2}>
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
                Category <span style={{ color: '#ef4444', marginLeft: '4px' }}>*</span>
              </Typography>
              <Select
                value={editState.feedback_type}
                onChange={(e) => {
                  setEditState((prev) => ({ ...prev, feedback_type: e.target.value }));
                  if (formErrors?.feedback_type)
                    setFormErrors((prev) => ({ ...prev, feedback_type: null }));
                }}
                displayEmpty
                fullWidth
                disabled={isSaving}
                sx={softTextFieldSx}
              >
                <MenuItem value="" disabled>
                  Select Category
                </MenuItem>
                <MenuItem value="LLM Fine Tuning">LLM Fine Tuning</MenuItem>
                <MenuItem value="Agent Prompting">Agent Prompting</MenuItem>
                <MenuItem value="Feedback as Context">Feedback as Context</MenuItem>
              </Select>
              {formErrors?.feedback_type && (
                <Typography
                  variant="caption"
                  color="error"
                  sx={{ mt: 0.5, fontSize: '12px', display: 'block' }}
                >
                  {formErrors?.feedback_type}
                </Typography>
              )}
            </Box>
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
                Feedback Details
              </Typography>
              <TextField
                multiline
                rows={3}
                value={editState.feedback}
                fullWidth
                disabled
                placeholder="Enter feedback"
                sx={{
                  ...softMultilineFieldSx,
                  '& .MuiInputBase-root.Mui-disabled': {
                    cursor: 'not-allowed',
                    backgroundColor: '#f8fafc',
                  },
                  '& .MuiInputBase-input.Mui-disabled': {
                    cursor: 'not-allowed',
                    WebkitTextFillColor: '#64748b',
                  },
                }}
              />
            </Box>
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
                Status <span style={{ color: '#ef4444', marginLeft: '4px' }}>*</span>
              </Typography>
              <Select
                value={editState.status}
                onChange={(e) => {
                  setEditState((prev) => ({ ...prev, status: e.target.value }));
                  if (formErrors?.status) setFormErrors((prev) => ({ ...prev, status: null }));
                }}
                fullWidth
                disabled={isSaving}
                sx={softTextFieldSx}
              >
                <MenuItem value="open">Open</MenuItem>
                <MenuItem value="in_progress">In Progress</MenuItem>
                <MenuItem value="closed">Closed</MenuItem>
              </Select>
              {formErrors.status && (
                <Typography
                  variant="caption"
                  color="error"
                  sx={{ mt: 0.5, fontSize: '12px', display: 'block' }}
                >
                  {formErrors.status}
                </Typography>
              )}
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 1 }}>
          <Button
            onClick={() => setEditingRow(null)}
            variant="outlined"
            disabled={isSaving}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveEdit}
            variant="contained"
            disabled={isSaving || !hasChanges}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              boxShadow: 'none',
              '&.Mui-disabled': { pointerEvents: 'auto', cursor: 'not-allowed' },
            }}
            startIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : null}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDeleteDialog
        open={!!deletingRow}
        onClose={() => setDeletingRow(null)}
        onConfirm={handleDeleteConfirm}
        titleName="feedback"
        itemName={`feedback "${
          deletingRow?.feedback ? deletingRow.feedback.split(' ').slice(0, 3).join(' ') + '...' : ''
        }"`}
      />
    </Box>
  );
};

export default FeedbackInbox;
